<template>
    <div>
        <!-- Affichage du Learning Object actuel -->
        <v-row>
            <v-col cols="12">
                <div v-html="previewHTML" />
            </v-col>
        </v-row>

        <!-- Boutons de navigation Précédent / Suivant -->
        <v-row justify="center" class="mt-2 pb-4">
            <v-btn
                :readonly="currentSlide === 0"
                :disabled="currentSlide === 0"
                @click="goToPrevious"
                class="mr-2 elevation-0"
                dark
                rounded="xl"
                small
                variant="flat"
                density="compact"
            >
                Précédent
            </v-btn>
            <span class="mt-1">
                {{ currentSlide + 1 }} / {{ learningModule.learningObjects.length }}
            </span>
            <v-btn
                :readonly="currentSlide === learningModule.learningObjects.length - 1"
                :disabled="currentSlide === learningModule.learningObjects.length - 1"
                @click="goToNext"
                class="ml-2 elevation-0"
                dark
                rounded="xl"
                small
                variant="flat"
                density="compact"
            >
                Suivant
            </v-btn>
        </v-row>
    </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch, toNative } from "vue-facing-decorator";
import TUTORIALS_MODULE from "../../graphql/tutorials/TutorialsModule.gql";
import UPSERT_ACTIVITY_STEP_COUNT from "../../graphql/usersActivities/UpsertUserActivityStepCount.gql";
import { Apollo } from "../../decorators";

@Component({
    emits: ["upsert-arcade-events"],
})
export class TutorialsModule extends Vue {
    @Prop({ default: null }) learningModuleId!: string | null;
    @Prop({ default: null }) learningObjectId!: string;
    // @Prop({ default: 0 }) currentSlide;

    currentSlide = 0;
    lastArcadeEvents: Array<any> = []; // Stockage des événements arcade
    hasNavigatedToSavedStep: Map<string, boolean> = new Map();
    arcadeSteps: Array<any> = [];

    get currentUser() {
        return this.$store.state.user;
    }

    get currentLearningObject() {
        if (this.learningModule.learningObjects.length) {
            return (
                this.learningModule.learningObjects[this.currentSlide] || {
                    id: "",
                    url: "",
                    embedCode: "",
                    stepCount: 0,
                    type: "IFRAM",
                    UserActivity: [] as any[],
                }
            );
        }
    }

    /** Determines if the preview should be displayed.
     * @returns A boolean indicating if the preview is enabled.
     */
    get displayPreview() {
        return !!this.previewHTML;
    }

    /** Retrieves the HTML for the tutorial preview based on its type.
     * @returns A string containing the HTML for the preview.
     */
    get previewHTML() {
        if (this.currentLearningObject?.url.includes("arcade.software")) {
            this.currentLearningObject.type = "IFRAM";

            return this.currentLearningObject.embedCode;
        } else if (this.currentLearningObject?.url.includes("docs.google.com")) {
            this.currentLearningObject.type = "IFRAM";
            return `<div style="height: 60%; width: 100%">${this.currentLearningObject.embedCode}</div>`;
        } else if (this.currentLearningObject?.url.includes("amazonaws.com/video")) {
            this.currentLearningObject.type = "VIDEO";
            return `<video src="${this.currentLearningObject.url}" controls style="width: 100%; height: 100%;"></video>`;
        } else {
            return "";
        }
    }

    beforeMount() {
        window.addEventListener("message", this.handleMessage);

        if (this.learningObjectId && this.learningModule?.learningObjects?.length) {
            const index = this.learningModule.learningObjects.findIndex(
                (obj: any) => obj.id === this.learningObjectId,
            );
            if (index !== -1) {
                this.currentSlide = index;
            }
        }
    }

    beforeDestroy() {
        if (this.learningObjectId) {
            this.learningObjectId = null;
        }
        if (this.lastArcadeEvents && this.lastArcadeEvents.length) {
            this.$emit("upsert-arcade-events", this.lastArcadeEvents);
        }
    }

    // Récupération des données du LearningModule
    @Apollo({
        query() {
            return TUTORIALS_MODULE;
        },
        variables() {
            return {
                id: this.learningModuleId,
            };
        },
        skip() {
            return !this.learningModuleId;
        },
        specificResult({ data, loading }: any) {
            if (!loading && data?.tutorialsModule?.tutorialsModule) {
                this.learningModule = { ...data.tutorialsModule.tutorialsModule };
            } else {
                this.learningModule = {
                    id: "",
                    name: "",
                    description: "",
                    difficulty: 0,
                    status: "",
                    profileType: "",
                    learningObjects: [] as string[],
                    key: "",
                };
            }
        },
    })
    learningModule: any = {
        id: "",
        name: "",
        description: "",
        difficulty: 0,
        status: "",
        profileType: "",
        learningObjects: [] as string[],
        key: "",
    };

    // Fonction pour recevoir les messages Arcade
    handleMessage(event: any) {
        let eventData;
        try {
            eventData = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        } catch (e) {
            console.error("Erreur de parsing JSON:", e);
            return;
        }

        if (event.origin !== "https://demo.arcade.software") {
            console.warn("Événement ignoré, mauvaise origine:", event.origin);
            return;
        }

        if (eventData?.event === "arcade-state-update") {
            this.handleArcadeStepUpdate(eventData);
        } else {
            console.log("Autre événement capturé:", eventData);
        }
    }

    // Méthode pour gérer la mise à jour des étapes Arcade
    async handleArcadeStepUpdate(event: any) {
        const currentObject = this.learningModule.learningObjects[this.currentSlide];

        if (currentObject?.id && currentObject.url.includes("arcade.software")) {
            let userActivity = currentObject?.UserActivity?.[0];

            // Vérification de l'activité utilisateur
            if (!userActivity) {
                const { data } = await this.$apollo.mutate({
                    mutation: UPSERT_ACTIVITY_STEP_COUNT,
                    variables: {
                        id: "",
                        userId: this.currentUser.id,
                        learningObjectId: currentObject.id,
                        userStepCount: 0,
                        tutorialStepCount: currentObject.stepCount || 0,
                    },
                });

                if (data?.upsertUserActivityStepCount) {
                    userActivity = data.upsertUserActivityStepCount;

                    currentObject.UserActivity[0] = {
                        id: userActivity.id,
                        stepCount: userActivity.stepCount,
                    };
                }
            }

            // Assurer que nous traitons l'événement uniquement si l'iframe correspond à celle de l'objet actuel
            if (userActivity?.id && currentObject.id && event?.arcade) {
                const steps = event?.arcade?.steps || [];
                if (steps.length > 0 && !this.hasNavigatedToSavedStep.get(currentObject.id)) {
                    const activeStep = steps.find((step) => step.order === userActivity.stepCount);

                    if (activeStep?.id) {
                        await this.checkIframeAndNavigate(activeStep.id);
                        this.hasNavigatedToSavedStep.set(currentObject.id, true);
                    }
                }

                const activeStep = steps.find((step) => step.isActive);

                if (
                    activeStep &&
                    activeStep.order >= userActivity.stepCount &&
                    activeStep.order <= currentObject.stepCount
                ) {
                    this.storeArcadeEvent(currentObject.id, activeStep.order, userActivity.id);
                }
            }
        }
    }

    // Stocker l'événement pour chaque tutoriel
    storeArcadeEvent(learningObjectId: string, stepCount: number, userActivityId) {
        const eventIndex = this.lastArcadeEvents.findIndex(
            (event) => event.learningObjectId === learningObjectId,
        );

        if (eventIndex === -1) {
            this.lastArcadeEvents.push({ learningObjectId, stepCount, userActivityId });
        } else {
            this.lastArcadeEvents[eventIndex].stepCount = stepCount;
        }
    }

    removeArcadeListener() {
        window.removeEventListener("message", this.handleMessage);
    }

    // Naviguer vers le tutoriel précédent
    goToPrevious() {
        if (this.currentSlide > 0) {
            this.currentSlide--;
        }
    }

    // Naviguer vers le tutoriel suivant
    goToNext() {
        if (this.currentSlide < this.learningModule.learningObjects.length - 1) {
            this.currentSlide++;
        }
    }

    async checkIframeAndNavigate(stepId: string) {
        if (!stepId) {
            return;
        }

        return new Promise((resolve, reject) => {
            let attempts = 10;
            const prisma2Interval = setInterval(() => {
                const iframes = document.querySelectorAll("iframe");
                let iframe = Array.from(iframes).find((frame) =>
                    frame.src.includes("arcade.software"),
                );
                if (iframe) {
                    if (iframe.contentWindow) {
                        if (iframe.contentWindow?.postMessage) {
                            iframe.contentWindow.postMessage(
                                {
                                    event: "navigate-to-step",
                                    stepId: stepId,
                                },
                                "*",
                            );
                        } else {
                            debugger;
                        }
                        clearInterval(prisma2Interval);
                        resolve(null);
                    } else {
                        console.error("iframe without contentWindow");
                    }
                } else {
                    attempts--;
                    if (attempts <= 0) {
                        clearInterval(prisma2Interval);
                        resolve(null);
                    }
                }
            }, 100);
        });
    }

    @Watch("learningModule", { deep: true })
    onLearningObjectIdChange() {
        debugger;
        if (this.learningObjectId && this.learningModule?.learningObjects?.length) {
            const index = this.learningModule.learningObjects.findIndex(
                (obj: any) => obj.id === this.learningObjectId,
            );
            if (index !== -1) {
                this.currentSlide = index;
            }
        }
    }
}

export default toNative(TutorialsModule);
</script>

<style>
.pagination-info {
    font-size: 16px;
    font-weight: 500;
    padding: 0 16px;
    color: #333;
    display: flex;
    align-items: center;
}
</style>
